import React, { useEffect, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import './homepage.css';

const baseURL = process.env.REACT_APP_BASE_URL;
const endpoint_add = '/add_conversation';
const VERCEL_FUNCTION_URL = 'https://demo-agent-bedrock.vercel.app/api/serpapi';

const HomePage = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const [conversations, setConversations] = useState([]);
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [debugData, setDebugData] = useState(null);
    const [showDebug, setShowDebug] = useState(false);
    const [searchStats, setSearchStats] = useState(null);

    const addConversation = async () => {
        try {
            let link_server = `${baseURL}${endpoint_add}`
            const response = await fetch(link_server, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id_user: user.username
                })
            });
            const data = await response.json();
            const id_conversation = data.id_conversation;
            window.location.href = `/chat/${id_conversation}`;
        } catch (error) {
            console.error('Error creating new conversation:', error);
        }
    };

    const fetchPharmaNews = async () => {
        try {
            setLoading(true);
            setError(null);
            setShowDebug(false);

            // Principales queries farmacéuticas
            const pharmaQueries = [
                // Grandes laboratorios farmacéuticos
                '("Pfizer México" OR "Novartis México" OR "Roche México" OR "Johnson & Johnson México" OR "AstraZeneca México")',

                // Distribuidores y cadenas farmacéuticas
                '("Farmacias Guadalajara" OR "Farmacias del Ahorro" OR "Farmacias Benavides" OR "Sanborns" OR NADRO OR Marzam)',

                // Regulaciones y organismos sanitarios
                'COFEPRIS AND (medicamentos OR fármacos OR aprobación OR registro sanitario) OR "Comisión Federal" OR "regulación sanitaria"',

                // Tendencias y estrategia de la industria farmacéutica
                '"industria farmacéutica mexicana" AND (inversión OR desarrollo OR biotecnología OR genéricos OR innovación)',

                // Acceso a medicamentos y políticas de salud
                '("acceso a medicamentos" AND México) OR "IMSS medicamentos" OR "ISSSTE" OR "sector salud" OR "política de medicamentos"'
            ];

            const pharmaKeywords = [
                // Laboratorios Farmacéuticos Multinacionales
                'Pfizer', 'Novartis', 'Roche', 'Johnson & Johnson', 'AstraZeneca', 'Bayer', 'GSK', 'Merck',
                'Sanofi', 'Bristol Myers', 'Eli Lilly', 'Abbott', 'Takeda', 'Boehringer',

                // Laboratorios Nacionales
                'Pisa', 'Probiomed', 'Silanes', 'Liomont', 'Landsteiner', 'Chinoin', 'Carnot',

                // Cadenas Farmacéuticas
                'Farmacias Guadalajara', 'Farmacias del Ahorro', 'Farmacias Benavides', 'Sanborns',
                'Cruz Verde', 'Farmatodo', 'farmacia', 'farmacias',

                // Distribuidores Farmacéuticos
                'NADRO', 'Marzam', 'Casa Saba', 'distribución farmacéutica', 'mayorista farmacéutico',

                // Organismos Regulatorios
                'COFEPRIS', 'Comisión Federal', 'CANIFARMA', 'AMIIF', 'ANAFARMEX',

                // Tipos de Medicamentos
                'medicamentos', 'fármacos', 'genéricos', 'biotecnológicos', 'biosimilares',
                'medicina especializada', 'oncológicos', 'diabetes', 'cardiovascular',

                // Conceptos de la Industria
                'industria farmacéutica', 'salud', 'investigación clínica', 'desarrollo farmacéutico',
                'registro sanitario', 'farmacovigilancia', 'precio de medicamentos',

                // Instituciones de Salud
                'IMSS', 'ISSSTE', 'Sector Salud', 'Secretaría de Salud', 'Instituto Nacional',

                // Términos Económicos Farmacéuticos
                'inversión farmacéutica', 'patentes', 'exclusividad', 'licencias', 'transferencia tecnológica',
                'mercado farmacéutico', 'acceso a medicamentos'
            ];

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);

            const response = await fetch(VERCEL_FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                signal: controller.signal,
                body: JSON.stringify({
                    queries: pharmaQueries,
                    keywords: pharmaKeywords
                })
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    error: `HTTP ${response.status}: ${response.statusText}`
                }));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            setDebugData(data);

            if (data.success && data.articles && data.articles.length > 0) {
                const processedArticles = data.articles.map((article, index) => ({
                    ...article,
                    image: article.image || `https://picsum.photos/350/200?random=${Date.now() + index}`,
                    url: article.url && article.url !== '#' && article.url.startsWith('http')
                        ? article.url
                        : null
                }));

                setNews(processedArticles);
                setLastUpdate(new Date());

                if (data.metadata) {
                    setSearchStats(data.metadata);
                }

            } else if (data.success === false) {
                console.warn('No se encontraron noticias farmacéuticas relevantes:', data.message);
                setError(`${data.message || 'No se encontraron noticias farmacéuticas relevantes de la última semana.'}`);

                if (data.debug) {
                    setSearchStats(data.debug);
                }
                setFallbackNews();

            } else {
                throw new Error(data.error || 'Respuesta inesperada de la función');
            }

        } catch (error) {
            console.error('Error obteniendo noticias farmacéuticas:', error);
            if (error.name === 'AbortError') {
                setError('La búsqueda tardó demasiado tiempo. Las búsquedas con filtros estrictos pueden tardar más.');
            } else {
                setError(`Error: ${error.message}`);
            }
            setFallbackNews();
        } finally {
            setLoading(false);
        }
    };

    const setFallbackNews = () => {
        const fallbackNews = [
            {
                title: 'COFEPRIS aprueba nuevo medicamento biotecnológico para diabetes tipo 2 con tecnología innovadora',
                description: 'La Comisión Federal para la Protección contra Riesgos Sanitarios autoriza el registro de un fármaco desarrollado con inteligencia artificial que promete mejor control glucémico.',
                url: null,
                publishedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
                source: 'Pharma México (Ejemplo)',
                image: 'https://picsum.photos/350/200?random=3001',
                position: 1,
                daysAgo: 1
            },
            {
                title: 'Farmacias Guadalajara anuncia expansión digital con servicio de entrega en 2 horas',
                description: 'La cadena farmacéutica líder en México implementa nueva tecnología para competir con plataformas digitales, cubriendo 15 ciudades adicionales.',
                url: null,
                publishedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                source: 'Industria Farmacéutica (Ejemplo)',
                image: 'https://picsum.photos/350/200?random=3002',
                position: 2,
                daysAgo: 2
            },
            {
                title: 'IMSS negocia reducción de precios de medicamentos oncológicos con laboratorios internacionales',
                description: 'El instituto busca mejorar el acceso a tratamientos especializados mediante acuerdos de compra consolidada con Pfizer, Roche y Novartis.',
                url: null,
                publishedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                source: 'Salud México (Ejemplo)',
                image: 'https://picsum.photos/350/200?random=3003',
                position: 3,
                daysAgo: 3
            }
        ];

        setNews(fallbackNews);
        setLastUpdate(new Date());
    };

    useEffect(() => {
        fetchPharmaNews();

        const interval = setInterval(() => {
            fetchPharmaNews();
        }, 2 * 60 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Hace poco';
        }
    };

    const truncateText = (text, maxLength = 150) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const handleRefresh = () => {
        fetchPharmaNews();
    };

    const getDaysAgoText = (daysAgo) => {
        if (daysAgo === 0) return 'Hoy';
        if (daysAgo === 1) return 'Ayer';
        return `Hace ${daysAgo} días`;
    };

    return (
        <main className="homepage-main">
            <div className="homepage">
                {/* Header Section */}
                <div className="header-section">
                    <h1 className="main-title">
                        Sector Farmacéutico México
                    </h1>
                    <p className="subtitle">
                        Noticias de la última semana del sector farmacéutico mexicano con filtros de relevancia estrictos
                    </p>

                    {/* Status indicator */}
                    <div className="status-indicator">
                        <div className="status-dot"></div>
                        {lastUpdate && !loading && (
                            <span className="status-text">
                                Actualizado: {formatDate(lastUpdate)}
                            </span>
                        )}
                    </div>

                    {/* Search stats */}
                    {searchStats && (
                        <div className="search-stats">
                            Queries: {searchStats.queriesExecuted || 'N/A'} |
                            Keywords: {searchStats.keywordFilters || 'N/A'} |
                            Filtros: Título 15, Desc 30, 2+ keywords farmacéuticos, última semana
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="action-buttons">
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="btn-primary"
                        >
                            {loading ? 'Procesando...' : 'Actualizar Noticias'}
                        </button>

                        <button
                            onClick={addConversation}
                            className="btn-secondary"
                        >
                            Nueva Conversación
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="loading-container">
                        <div className="loading-spinner">⚡</div>
                        <div className="loading-title">
                            Ejecutando múltiples búsquedas farmacéuticas...
                        </div>
                        <div className="loading-subtitle">
                            Aplicando filtros de relevancia estrictos
                        </div>
                        <div className="loading-note">
                            Esto puede tardar 30-45 segundos
                        </div>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="error-container">
                        {error}
                        <div className="error-details">
                            <strong>Información sobre la búsqueda avanzada:</strong><br />
                            • La función ejecuta 5 queries diferentes con filtros estrictos<br />
                            • Requiere 2+ keywords farmacéuticos por artículo<br />
                            • Filtra títulos cortos y descripciones vacías<br />
                            • Puede tardar más tiempo pero da resultados más relevantes
                        </div>
                    </div>
                )}

                {/* News Grid */}
                <div className="news-grid">
                    {news.map((article, index) => (
                        <div
                            key={`${article.title}-${index}`}
                            className={`news-card fade-in-up ${!article.url ? 'disabled' : ''}`}
                            style={{ animationDelay: `${index * 0.05}s` }}
                            onClick={() => article.url && window.open(article.url, '_blank')}
                        >
                            {/* Image Container */}
                            <div className="news-image-container">
                                <img
                                    className="news-image"
                                    src={article.image}
                                    alt={article.title}
                                    onError={(e) => {
                                        e.target.src = `https://via.placeholder.com/320x160/2c5530/ffffff?text=Pharma+MX+${article.position || index + 1}`;
                                    }}
                                />

                                {article.daysAgo !== undefined && (
                                    <div className="image-overlay-date">
                                        {getDaysAgoText(article.daysAgo)}
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="news-content">
                                {/* Source and Date */}
                                <div className="news-meta">
                                    <span className="news-source">
                                        {article.source}
                                    </span>
                                    <span className="news-date">
                                        {formatDate(article.publishedAt)}
                                    </span>
                                </div>

                                {/* Title */}
                                <h3 className="news-title">
                                    {article.title}
                                </h3>

                                {/* Footer */}
                                <div className="news-footer">
                                    <div className={`news-read-link ${!article.url ? 'disabled' : ''}`}>
                                        {!article.url ? 'Ejemplo' : 'Leer'}
                                    </div>
                                    <div className="news-tag">
                                        FARMACIA
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Empty State */}
                {!loading && news.length === 0 && !error && (
                    <div className="empty-state">
                        <div className="empty-icon">💊</div>
                        <div className="empty-title">
                            Los filtros estrictos no encontraron noticias farmacéuticas relevantes de la última semana.
                        </div>
                        <div className="empty-subtitle">
                            Intenta refrescar más tarde o verifica el Debug Avanzado para más información.
                        </div>
                    </div>
                )}

                {/* Footer Info */}
                <div className="footer-info">
                    <div className="footer-main">
                        <strong>Actualización automática:</strong> Cada 2 horas
                    </div>
                    <div className="footer-detail">
                        <strong>Período:</strong> Últimos 7 días con verificación de fechas
                    </div>
                </div>
            </div>
        </main>
    );
};

export default HomePage;